/**
 * Restaurant Store
 * 
 * Manages restaurant context for the application.
 * Restaurant data is synced from Laravel to Firebase when table-ordering is activated.
 * 
 * Firebase Schema:
 * restaurants/{slug}/
 *   ├── id (Laravel restaurant ID)
 *   ├── slug
 *   ├── name
 *   ├── logo
 *   ├── settings
 *   │   ├── currency: "NOK"
 *   │   ├── tableOrderingEnabled: true
 *   │   └── ...
 *   ├── categories/
 *   │   └── {categoryId}: { name, sortOrder, ... }
 *   └── menuItems/
 *       └── {itemId}: { name, price, categoryId, ... }
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { db } from '@/services/firebase'
import { ref as dbRef, get } from 'firebase/database'

export const useRestaurantStore = defineStore('restaurant', () => {
  // State
  const restaurant = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const lastFetched = ref(null)

  // Getters
  const isLoaded = computed(() => restaurant.value !== null)
  const restaurantId = computed(() => restaurant.value?.id)
  const restaurantSlug = computed(() => restaurant.value?.slug)
  const restaurantName = computed(() => restaurant.value?.name || 'Restaurant')
  const restaurantLogo = computed(() => restaurant.value?.logo)
  const settings = computed(() => restaurant.value?.settings || {})
  const isTableOrderingEnabled = computed(() => settings.value?.tableOrderingEnabled ?? false)

  // Actions
  
  /**
   * Fetch restaurant data from Firebase by slug
   * @param {string} slug - Restaurant URL slug
   * @returns {Promise<Object>} Restaurant data
   */
  const fetchRestaurant = async (slug) => {
    if (!slug) {
      throw new Error('Restaurant slug is required')
    }

    // Clear any previous cache for this slug to avoid stale data issues
    const cacheKey = `dandero_restaurant_${slug}`
    
    // Check cache (5 minute TTL)
    const cached = localStorage.getItem(cacheKey)
    if (cached) {
      try {
        const { data, timestamp } = JSON.parse(cached)
        const age = Date.now() - timestamp
        if (age < 5 * 60 * 1000) { // 5 minutes
          restaurant.value = data
          lastFetched.value = timestamp
          console.log('[RestaurantStore] Loaded from cache:', slug, data)
          return data
        } else {
          // Cache expired, remove it
          localStorage.removeItem(cacheKey)
        }
      } catch (e) {
        localStorage.removeItem(cacheKey)
      }
    }

    loading.value = true
    error.value = null
    console.log('[RestaurantStore] Fetching from Firebase:', slug)

    try {
      const restaurantRef = dbRef(db, `restaurants/${slug}`)
      console.log('[RestaurantStore] Firebase ref:', `restaurants/${slug}`)
      const snapshot = await get(restaurantRef)
      console.log('[RestaurantStore] Snapshot exists:', snapshot.exists())

      if (!snapshot.exists()) {
        throw new Error('Restaurant not found')
      }

      const data = snapshot.val()
      console.log('[RestaurantStore] Data:', data)

      // Verify table ordering is enabled
      if (!data.settings?.tableOrderingEnabled) {
        throw new Error('Table ordering is not enabled for this restaurant')
      }

      restaurant.value = {
        ...data,
        slug // Ensure slug is included
      }
      lastFetched.value = Date.now()

      // Cache the data
      localStorage.setItem(cacheKey, JSON.stringify({
        data: restaurant.value,
        timestamp: lastFetched.value
      }))

      console.log('[RestaurantStore] Success:', restaurant.value)
      return restaurant.value
    } catch (e) {
      console.error('[RestaurantStore] Error:', e.message)
      error.value = e.message
      throw e
    } finally {
      loading.value = false
    }
  }

  /**
   * Clear restaurant context
   */
  const clearRestaurant = () => {
    restaurant.value = null
    error.value = null
    lastFetched.value = null
  }

  /**
   * Check if restaurant data needs refresh
   */
  const needsRefresh = () => {
    if (!lastFetched.value) return true
    const age = Date.now() - lastFetched.value
    return age > 5 * 60 * 1000 // 5 minutes
  }

  return {
    // State
    restaurant,
    loading,
    error,
    lastFetched,
    // Getters
    isLoaded,
    restaurantId,
    restaurantSlug,
    restaurantName,
    restaurantLogo,
    settings,
    isTableOrderingEnabled,
    // Actions
    fetchRestaurant,
    clearRestaurant,
    needsRefresh
  }
})
